import * as React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { useStyle } from "./style";
import { ChangeEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-responsive";

interface IProps {
  onChangePageNumber: (event: ChangeEvent<unknown>, page: number) => void;
  onChangePageSize: (
    event: SelectChangeEvent<number>,
    child: ReactNode
  ) => void;
  style?: React.CSSProperties;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  pagesCount: number;
  isFromDocumentsOrJobs?: boolean;
  router?: any;
  isMobile?: boolean;
  initialPageSize: number;
  pageSizeOptions2?: number[];
}

const GoMakePagination = ({
  onChangePageSize,
  onChangePageNumber,
  style,
  page,
  setPage,
  pageSize,
  pagesCount,
  isFromDocumentsOrJobs = false,
  router,
  isMobile: isMobileProp,
  initialPageSize,
  pageSizeOptions2 = []
}: IProps) => {
  const detectedMobile = useIsMobile();
  const isMobile = isMobileProp ?? detectedMobile;
  const { classes } = useStyle(isMobile);
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const pageOptions = Array.from(
    { length: pagesCount },
    (_, index) => index + 1
  );
  const themeRTL = createTheme({
    direction: dir,
  });

  const defaultPageSizes = [10, 15, 20, 30];

  const pageSizeOptions = defaultPageSizes.includes(initialPageSize)
    ? defaultPageSizes
    : [...defaultPageSizes, initialPageSize, ...pageSizeOptions2].sort((a, b) => a - b);

  return (
    <div style={{ ...classes.paginationStyle, ...style }}>
      <ThemeProvider theme={themeRTL}>
        <Pagination
          count={pagesCount}
          variant="outlined"
          color="primary"
          page={page}
          onChange={onChangePageNumber}
          size={isMobile ? "small" : "medium"}
          siblingCount={isMobile ? 0 : 1}
          boundaryCount={isMobile ? 1 : 1}
          sx={{
            ...(isMobile && {
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "center",
              "& .MuiPagination-ul": {
                flexWrap: "nowrap",
                justifyContent: "center",
              },
              "& .MuiPaginationItem-root": {
                minWidth: 28,
                height: 28,
                margin: "0 1px",
                fontSize: 12,
              },
            }),
          }}
        />
      </ThemeProvider>
      <Box style={classes.selectsContainerStyle}>
        <FormControl
          variant="standard"
          sx={{
            m: isMobile ? 0 : 1,
            minWidth: isMobile ? 64 : 80,
            ...(isMobile && {
              "& .MuiInputLabel-root": { fontSize: 12 },
              "& .MuiInputBase-root": { fontSize: 13 },
            }),
          }}
        >
          <InputLabel id="demo-simple-select-standard-label">
            {t("general.lines")}
          </InputLabel>

          <Select
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            value={pageSize}
            onChange={onChangePageSize}
            label={t("general.lines")}
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          variant="standard"
          sx={{
            m: isMobile ? 0 : 1,
            minWidth: isMobile ? 64 : 80,
            ...(isMobile && {
              "& .MuiInputLabel-root": { fontSize: 12 },
              "& .MuiInputBase-root": { fontSize: 13 },
            }),
          }}
        >
          <InputLabel id="demo-simple-select-standard-label">
            {t("general.pageNumber")}
          </InputLabel>
          <Select
            labelId="demo-simple-select-standard-label"
            id="demo-simple-select-standard"
            value={page}
            onChange={(event) => {
              setPage(event.target.value as number);
              isFromDocumentsOrJobs &&
                router.push({
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    pageNumber: event.target.value as number,
                  },
                });
            }}
            label={t("general.pageNumber")}
          >
            {pageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </div>
  );
};

export { GoMakePagination };
